import React, { useCallback, useEffect, useState } from 'react';
import { Button, TextField, IconButton } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useAuth } from '../context/AuthContext';

const API_BASE = (process.env.REACT_APP_API_BASE || 'https://backend.parthmerchant.com').replace(/\/$/, '');

const tfSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    background: 'var(--bg-3)',
    transition: 'box-shadow 0.18s ease',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-2)' },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px var(--green-glow)',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--green-border)', borderWidth: '1px' },
    },
  },
  '& .MuiInputBase-input': {
    color: 'var(--text)',
    fontFamily: 'var(--font-sans)',
    fontSize: '0.88rem',
    '&::placeholder': { color: 'var(--text-3)', opacity: 1 },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--text-3)',
    fontFamily: 'var(--font-sans)',
    fontSize: '0.85rem',
    '&.Mui-focused': { color: 'var(--green)' },
  },
};

const BLANK = {
  hero: { greeting: '', nameFirst: '', nameLast: '', keywords: [] },
  about: { heading: '', paragraphs: [''], terminalTitle: '', terminalLines: [{ prompt: '', output: '', highlight: false }] },
  globe: { label: '', heading: '', sub: '' },
};

function ListEditor({ label, items, onChange, placeholder, multiline }) {
  const update = (i, val) => {
    const next = [...items];
    next[i] = val;
    onChange(next);
  };
  const add = () => onChange([...items, '']);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="admset-field">
      <span className="admset-label">{label}</span>
      <div className="sa-list-editor">
        {items.map((val, i) => (
          <div className="sa-list-row" key={i}>
            <TextField
              size="small"
              fullWidth
              multiline={multiline}
              minRows={multiline ? 2 : 1}
              value={val}
              placeholder={placeholder}
              onChange={e => update(i, e.target.value)}
              sx={tfSx}
            />
            <IconButton
              size="small"
              onClick={() => remove(i)}
              disabled={items.length <= 1}
              sx={{ color: 'var(--text-3)', '&:hover': { color: '#f87171', background: 'rgba(248,113,113,0.08)' } }}
            >
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </div>
        ))}
        <button type="button" className="sa-list-add" onClick={add}>
          <AddRoundedIcon sx={{ fontSize: 14 }} /> Add {label.toLowerCase().replace(/s$/, '')}
        </button>
      </div>
    </div>
  );
}

function TerminalLinesEditor({ items, onChange }) {
  const update = (i, patch) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const add = () => onChange([...items, { prompt: '', output: '', highlight: false }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="admset-field">
      <span className="admset-label">Terminal Lines</span>
      <div className="sa-list-editor">
        {items.map((line, i) => (
          <div className="sa-terminal-line-row" key={i}>
            <TextField
              size="small"
              fullWidth
              value={line.prompt || ''}
              placeholder="whoami"
              label="Prompt"
              onChange={e => update(i, { prompt: e.target.value })}
              sx={tfSx}
            />
            <TextField
              size="small"
              fullWidth
              value={line.output || ''}
              placeholder="engineer · ai builder · dog dad"
              label="Output"
              onChange={e => update(i, { output: e.target.value })}
              sx={tfSx}
            />
            <label className="sa-terminal-highlight">
              <input
                type="checkbox"
                checked={!!line.highlight}
                onChange={e => update(i, { highlight: e.target.checked })}
              />
              Highlight
            </label>
            <IconButton
              size="small"
              onClick={() => remove(i)}
              disabled={items.length <= 1}
              sx={{ color: 'var(--text-3)', '&:hover': { color: '#f87171', background: 'rgba(248,113,113,0.08)' } }}
            >
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </div>
        ))}
        <button type="button" className="sa-list-add" onClick={add}>
          <AddRoundedIcon sx={{ fontSize: 14 }} /> Add terminal line
        </button>
      </div>
    </div>
  );
}

function KeywordEditor({ keywords, onChange }) {
  const [draft, setDraft] = useState('');

  const addKeyword = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...keywords, v]);
    setDraft('');
  };

  const removeKeyword = (i) => onChange(keywords.filter((_, idx) => idx !== i));

  return (
    <div className="admset-field">
      <span className="admset-label">Keywords</span>
      <div className="sa-chip-row">
        {keywords.map((k, i) => (
          <span className="sa-chip" key={`${k}-${i}`}>
            {k}
            <button type="button" className="sa-chip-remove" onClick={() => removeKeyword(i)}>
              <CloseRoundedIcon sx={{ fontSize: 12 }} />
            </button>
          </span>
        ))}
      </div>
      <div className="sa-chip-input-row">
        <TextField
          size="small"
          fullWidth
          value={draft}
          placeholder="Add a keyword and press Enter…"
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
          sx={tfSx}
        />
        <Button
          variant="outlined"
          onClick={addKeyword}
          sx={{ borderRadius: '6px', height: '40px', textTransform: 'none', fontFamily: 'var(--font-sans)', border: '1px solid var(--border)', color: 'var(--text-2)', '&:hover': { background: 'var(--bg-3)', borderColor: 'var(--border-2)', color: 'var(--text)' } }}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

export default function LandingContent() {
  const { token } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const showToast = useCallback((msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const authHeaders = useCallback(() => (
    token ? { Authorization: `Bearer ${token}` } : {}
  ), [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await fetch(`${API_BASE}/api/landing/content`, {
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setContent({
          hero: {
            greeting: data?.hero?.greeting || '',
            nameFirst: data?.hero?.nameFirst || '',
            nameLast: data?.hero?.nameLast || '',
            keywords: Array.isArray(data?.hero?.keywords) && data.hero.keywords.length ? data.hero.keywords : [],
          },
          about: {
            heading: data?.about?.heading || '',
            paragraphs: Array.isArray(data?.about?.paragraphs) && data.about.paragraphs.length ? data.about.paragraphs : [''],
            terminalTitle: data?.about?.terminalTitle || '',
            terminalLines: Array.isArray(data?.about?.terminalLines) && data.about.terminalLines.length
              ? data.about.terminalLines
              : [{ prompt: '', output: '', highlight: false }],
          },
          globe: {
            label: data?.globe?.label || '',
            heading: data?.globe?.heading || '',
            sub: data?.globe?.sub || '',
          },
        });
      } catch {
        if (!cancelled) { setContent(BLANK); setLoadError(true); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  const setHero = (patch) => setContent(c => ({ ...c, hero: { ...c.hero, ...patch } }));
  const setAbout = (patch) => setContent(c => ({ ...c, about: { ...c.about, ...patch } }));
  const setGlobe = (patch) => setContent(c => ({ ...c, globe: { ...c.globe, ...patch } }));

  const handleSave = async () => {
    if (!content) return;
    setSaving(true);
    try {
      const body = {
        hero: {
          ...content.hero,
          keywords: content.hero.keywords.map(k => k.trim()).filter(Boolean),
        },
        about: {
          heading: content.about.heading,
          paragraphs: content.about.paragraphs.map(p => p.trim()).filter(Boolean),
          terminalTitle: content.about.terminalTitle,
          terminalLines: content.about.terminalLines
            .map(l => ({ prompt: (l.prompt || '').trim(), output: (l.output || '').trim(), highlight: !!l.highlight }))
            .filter(l => l.prompt || l.output),
        },
        globe: { ...content.globe },
      };
      const res = await fetch(`${API_BASE}/api/landing/content`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `${res.status}` }));
        throw new Error(err.detail || `${res.status}`);
      }
      showToast('Landing page content saved', 'success');
    } catch (e) {
      showToast(e.message || 'Could not save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const Toast = toast ? (
    <div className={`boards-toast boards-toast-${toast.type}`}>{toast.msg}</div>
  ) : null;

  if (loading) {
    return (
      <div className="admset">
        {Toast}
        <div className="boards-empty">Loading landing content…</div>
      </div>
    );
  }

  return (
    <div className="admset">
      {Toast}

      <div className="admset-banner sa-landing-banner">
        <div className="admset-banner-id">
          <span className="admset-eyebrow">Admin</span>
          <h1 className="admset-name">Landing Content</h1>
          <p className="admset-email">Edit the copy that renders on parthmerchant.com's homepage.</p>
        </div>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={saving}
          sx={{
            borderRadius: '8px', height: '40px', textTransform: 'none', fontWeight: 600,
            fontFamily: 'var(--font-sans)', background: 'var(--green)', color: 'var(--btn-text)',
            px: 2.25, boxShadow: 'none',
            '&:hover': { background: 'var(--green)', opacity: 0.9, boxShadow: '0 0 0 3px var(--green-glow)' },
            '&.Mui-disabled': { background: 'var(--green)', color: 'var(--btn-text)', opacity: 0.5 },
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {loadError && (
        <p className="admset-hint" style={{ color: '#f87171', marginBottom: '1rem' }}>
          Could not load existing content — starting from a blank form. Saving will create it.
        </p>
      )}

      <div className="admset-grid">
        <div className="admset-card">
          <div className="admset-card-head"><h2>Hero</h2></div>
          <p className="admset-card-desc">The greeting, name, and tagline shown at the top of the homepage.</p>

          <div className="admset-field">
            <span className="admset-label">Greeting</span>
            <TextField
              size="small"
              fullWidth
              value={content.hero.greeting}
              placeholder="Hey, I'm"
              onChange={e => setHero({ greeting: e.target.value })}
              sx={tfSx}
            />
          </div>
          <div className="admset-field" style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <span className="admset-label">First name</span>
              <TextField
                size="small"
                fullWidth
                value={content.hero.nameFirst}
                placeholder="Parth"
                onChange={e => setHero({ nameFirst: e.target.value })}
                sx={tfSx}
              />
            </div>
            <div style={{ flex: 1 }}>
              <span className="admset-label">Last name</span>
              <TextField
                size="small"
                fullWidth
                value={content.hero.nameLast}
                placeholder="Merchant"
                onChange={e => setHero({ nameLast: e.target.value })}
                sx={tfSx}
              />
            </div>
          </div>
          <p className="admset-hint">Last name renders in the accent color next to the first name.</p>

          <KeywordEditor
            keywords={content.hero.keywords}
            onChange={keywords => setHero({ keywords })}
          />
        </div>

        <div className="admset-card">
          <div className="admset-card-head"><h2>About</h2></div>
          <p className="admset-card-desc">The About section's paragraphs and the animated terminal lines beside it.</p>

          <div className="admset-field">
            <span className="admset-label">Heading</span>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={content.about.heading}
              placeholder={'Building things\nthat matter.'}
              helperText="A newline splits it across two lines."
              onChange={e => setAbout({ heading: e.target.value })}
              sx={tfSx}
            />
          </div>

          <ListEditor
            label="Paragraphs"
            items={content.about.paragraphs}
            onChange={paragraphs => setAbout({ paragraphs })}
            placeholder="Write a paragraph…"
            multiline
          />

          <div className="admset-field">
            <span className="admset-label">Terminal window title</span>
            <TextField
              size="small"
              fullWidth
              value={content.about.terminalTitle}
              placeholder="parth@sf: ~/now"
              onChange={e => setAbout({ terminalTitle: e.target.value })}
              sx={tfSx}
            />
          </div>

          <TerminalLinesEditor
            items={content.about.terminalLines}
            onChange={terminalLines => setAbout({ terminalLines })}
          />
        </div>

        <div className="admset-card">
          <div className="admset-card-head"><h2>Globe Section</h2></div>
          <p className="admset-card-desc">Label, heading, and subheading above the location globe.</p>

          <div className="admset-field">
            <span className="admset-label">Label</span>
            <TextField
              size="small"
              fullWidth
              value={content.globe.label}
              placeholder="Travel · Photography · Cloud"
              onChange={e => setGlobe({ label: e.target.value })}
              sx={tfSx}
            />
          </div>
          <div className="admset-field">
            <span className="admset-label">Heading</span>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={content.globe.heading}
              placeholder={"Places I've\nmoved through."}
              helperText="A newline splits it across two lines; the second line is accent-colored."
              onChange={e => setGlobe({ heading: e.target.value })}
              sx={tfSx}
            />
          </div>
          <div className="admset-field">
            <span className="admset-label">Subheading</span>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={content.globe.sub}
              placeholder="A short subheading…"
              onChange={e => setGlobe({ sub: e.target.value })}
              sx={tfSx}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
